import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import type { ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import Label from 'LuxKit/Label/Label'
import TextLink from 'LuxKit/TextLink'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import TourTermsAndConditionsModal from 'components/OfferPage/TourV2/TourTermsAndConditionsModal'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { getTourCampaignConfig, isTourCampaignActive, TOUR_CAMPAIGN_SEARCH_FILTER_TAG } from 'constants/tours'
import ModalContext from 'contexts/ModalContext'
import PrefersReducedMotionContext from 'contexts/PrefersReducedMotionContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useAnywherePlace from 'hooks/useAnywherePlace'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { EmptyArray } from 'lib/array/arrayUtils'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'

const SLIDE_INTERVAL_MS = 4000

// Scales between the Figma tablet (282px of 768) and desktop (370px of 1140) photo widths
const PHOTO_WIDTH_FROM_TABLET = `calc(23.7% + ${rem(100)})`
const CARD_MIN_HEIGHT_DESKTOP = rem(160)

// Hides the carousel's "1 / 2" counter
const NO_IMAGE_COUNTER = <span />

// No fixed aspect ratio so the photo crops smoothly as the panel resizes
const PHOTO_IMAGE_PARAMS: ImageParams = {
  mobileWidth: '100vw',
  tabletWidth: '40vw',
  desktopWidth: '400px',
  quality: 'good',
  dpr: 2,
}

const Card = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: var(--border-radius-xl);
  background-color: var(--palette-neutral-surface-light-background);

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }

  ${mediaQueryUp.desktop} {
    min-height: ${CARD_MIN_HEIGHT_DESKTOP};
  }
`

const Photo = styled.div`
  position: relative;
  flex-shrink: 0;
  aspect-ratio: 375 / 117;
  container-type: size;
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    width: ${PHOTO_WIDTH_FROM_TABLET};
    aspect-ratio: auto;
  }
`

const PhotoCarousel = styled.div`
  position: absolute;
  inset: 0;
`

// Brand arch outline, sized as a share of the photo as in every Figma frame. Physical so it isn't mirrored in RTL.
const Arch = styled.div`
  position: absolute;
  /* oxlint-disable-next-line logical-css/no-physical-properties */
  left: 13%;
  inset-block-end: 12%;
  /* Grows when the photo is taller than in Figma so it still runs off the top */
  width: clamp(66%, 110cqh, 82%);
  aspect-ratio: 1;
  border: ${rem(1.5)} solid var(--palette-highlight-primary-dark-contrast);
  /* oxlint-disable-next-line logical-css/no-physical-shorthand */
  border-radius: 50% 50% 3% 50%;
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    border-width: ${rem(2)};
  }
`

const Content = styled(Group)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(28)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(20)} ${rem(40)};
  }
`

function renderItalic(chunks: ReactNode) {
  return <i key="italic">{chunks}</i>
}

function TourOfferCampaignBanner() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const isTourCampaignEnabled = isTourCampaignActive(region, config.BRAND)
  const campaign = useMemo(() => getTourCampaignConfig(intl), [intl])
  const formatCurrency = useCurrencyFormatter({ currency: campaign.flightCredit?.currencyCode })
  const termsAndConditions = campaign.tiers[0]?.termsAndConditions
  const slides = campaign.banner?.slides ?? EmptyArray
  const prefersReducedMotion = useContext(PrefersReducedMotionContext)
  const carouselRef = useRef<ImageCarouselRefInterface | null>(null)
  const { activeIndex: activeSlideIndex } = useCarouselAutoPlay(slides.length, SLIDE_INTERVAL_MS, { enabled: !prefersReducedMotion })

  const anywhere = useAnywherePlace()

  const searchUrl = useMemo(() => addQueryParamsToPath('/search/tours', {
    destinationId: anywhere.id,
    destinationName: anywhere.name,
    campaigns: TOUR_CAMPAIGN_SEARCH_FILTER_TAG,
  }), [anywhere])

  const images = useMemo(() => slides.map(slide => ({ id: slide.imageId })), [slides])

  // Hard cut between photos rather than the carousel's swipe
  useEffect(() => {
    carouselRef.current?.goToIndex(activeSlideIndex, 'instant')
  }, [activeSlideIndex])

  const openTermsAndConditionsModal = useCallback(() => {
    showModal(<TourTermsAndConditionsModal termsAndConditions={termsAndConditions} />)
  }, [showModal, termsAndConditions])

  if (!isTourCampaignEnabled || !campaign.flightCredit || !slides.length) {
    return null
  }

  const bookNowLabel = intl.formatMessage({
    id: 'U-9KPtl1MD7CvubXtkFp',
    defaultMessage: 'Book now',
    description: 'Call to action button on the tours early bird campaign banner that opens the campaign tour search',
  })

  const termsLink = <TextLink underline={false} onClick={openTermsAndConditionsModal}>
    <Caption as="span" variant="small" weight="bold" format="uppercase" colour="normal-contrast">
      {intl.formatMessage({
        id: '6vjX3YB6dn1UF2R7n0NV',
        defaultMessage: 'T&C’s apply.',
        description: 'Link on the tours early bird campaign banner that opens the full flight voucher terms and conditions',
      })}
    </Caption>
  </TextLink>

  return (
    <Card>
      <Photo>
        <PhotoCarousel>
          <ImageCarousel
            images={images}
            imageParams={PHOTO_IMAGE_PARAMS}
            carouselRef={carouselRef}
            arrowBehaviour="hidden"
            bottomLeft={NO_IMAGE_COUNTER}
            eagerLoadFirstImage
          />
        </PhotoCarousel>
        <Arch aria-hidden="true" />
      </Photo>
      <Content direction="vertical" desktopDirection="horizontal" gap={8} desktopGap={24} desktopVerticalAlign="center" grow>
        <Group direction="vertical" gap={8} horizontalAlign="start" grow>
          <Label kind="primary" variant="urgency">
            {intl.formatMessage({
              id: 'jB9uwc3DSUgJ6fkyBC0x',
              defaultMessage: 'On sale',
              description: 'Urgency label at the top of the tours early bird flight voucher campaign banner',
            })}
          </Label>
          <HeadingTextBlock variant="heading3" colour="normal-contrast">
            {intl.formatMessage(
              {
                id: 'eLpUsxRqIa-8czwHzDHG',
                defaultMessage: 'Book a 2027 tour now & receive a <i>flight voucher</i> up to {flightCreditValue}',
                description: 'Headline on the tours early bird campaign banner with the maximum flight voucher per person and italic flight voucher',
              },
              {
                flightCreditValue: formatCurrency(campaign.flightCredit.maxValuePerPerson, 'rounded'),
                i: renderItalic,
              },
            )}
          </HeadingTextBlock>
        </Group>
        <CSSBreakpoint max="tablet">
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            {termsLink}
            <TextLink to={searchUrl} size="medium" colour="normal-contrast">{bookNowLabel}</TextLink>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <Group direction="vertical" gap={8} horizontalAlign="center">
            <TextButton kind="primary" shape="round" size="large" to={searchUrl}>{bookNowLabel}</TextButton>
            {termsLink}
          </Group>
        </CSSBreakpoint>
      </Content>
    </Card>
  )
}

export default TourOfferCampaignBanner
